import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity, BarChart3, Bot, ClipboardList, LayoutDashboard, MessageSquare, Package, RefreshCw,
  ScrollText, Search, Settings2, ShieldCheck, Users, Sparkles, Zap, CheckCircle2
} from 'lucide-react';
import {
  Alert, AsyncBoundary, Badge, Button, Card, CardHeader, DataTable, EmptyState,
  Input, SectionTitle, Select, StatCard, StatGrid, Tabs
} from '../ui';
import {
  ACTOR_LABELS, EVENT_LABELS, OPEN_STATUSES, REQUEST_STATUS, REQUEST_STATUS_ORDER,
  adminService, formatDate, formatDateTime, formatMoney
} from '../../services/agencyService';
import CatalogoProductos from './CatalogoProductos';
import FichaCliente from './FichaCliente';
import SolicitudDetalle from './SolicitudDetalle';
import OperacionesIA from './OperacionesIA';
import WhatsAppMarketos from './WhatsAppMarketos';

// ⭐ Operación MARKETOS — centro de control del administrador (spec §1).
//
// Este componente es el shell: mantiene la navegación entre secciones y los
// datos compartidos (métricas, clientes, solicitudes), y delega cada sección
// pesada a su propio archivo. La razón de no partirlo más es que las tres
// secciones más usadas (resumen, clientes, solicitudes) comparten exactamente
// los mismos datos; separarlas implicaría o duplicar peticiones o montar un
// store global para un panel que hoy tiene un solo usuario concurrente.

const SECTIONS = [
  { id: 'resumen', label: 'Resumen', icon: <LayoutDashboard className="h-4 w-4" /> },
  { id: 'clientes', label: 'Clientes', icon: <Users className="h-4 w-4" /> },
  { id: 'catalogo', label: 'Catálogo', icon: <Package className="h-4 w-4" /> },
  { id: 'contratados', label: 'Productos contratados', icon: <ShieldCheck className="h-4 w-4" /> },
  { id: 'solicitudes', label: 'Solicitudes', icon: <ClipboardList className="h-4 w-4" /> },
  { id: 'mensajes', label: 'Bandeja', icon: <MessageSquare className="h-4 w-4" /> },
  { id: 'aprobados', label: 'Cambios aprobados', icon: <Activity className="h-4 w-4" /> },
  { id: 'metricas', label: 'Métricas', icon: <BarChart3 className="h-4 w-4" /> },
  { id: 'auditoria', label: 'Auditoría', icon: <ScrollText className="h-4 w-4" /> },
  { id: 'whatsapp', label: 'WhatsApp', icon: <MessageSquare className="h-4 w-4" /> },
  { id: 'ia', label: 'IA y MCP', icon: <Bot className="h-4 w-4" /> },
  { id: 'configuracion', label: 'Configuración', icon: <Settings2 className="h-4 w-4" /> }
];

export default function OperacionMarketos() {
  const [section, setSection] = useState('resumen');
  const [metrics, setMetrics] = useState(null);
  const [clients, setClients] = useState([]);
  const [requests, setRequests] = useState([]);
  const [inboxMessages, setInboxMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [openClientId, setOpenClientId] = useState(null);
  const [openRequestId, setOpenRequestId] = useState(null);

  const [clientSearch, setClientSearch] = useState('');
  const [requestStatusFilter, setRequestStatusFilter] = useState('abiertas');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Se piden TODAS las solicitudes (sin filtro de estado) una sola vez y el
      // filtrado se hace en memoria. Son decenas, no miles: filtrar en el
      // cliente evita una petición por cada clic en una pestaña de estado.
      // La bandeja es complementaria: un error al consultar un mensaje nunca
      // debe ocultar clientes, métricas y solicitudes de toda la operación.
      const [metricsResult, clientsResult, requestsResult] = await Promise.all([
        adminService.metrics(),
        adminService.listClients(),
        adminService.listRequests()
      ]);
      setMetrics(metricsResult);
      setClients(clientsResult.clients || []);
      setRequests(requestsResult.requests || []);
      try {
        const inboxResult = await adminService.listInboxMessages();
        setInboxMessages(inboxResult.messages || []);
      } catch {
        // La bandeja se muestra vacía y el resto del centro de control sigue
        // operativo mientras el servidor registra el problema concreto.
        setInboxMessages([]);
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filteredClients = useMemo(() => {
    const term = clientSearch.trim().toLowerCase();
    if (!term) return clients;
    return clients.filter((client) =>
      [client.name, client.display_name, client.email, client.industry, client.primary_domain]
        .filter(Boolean).some((field) => String(field).toLowerCase().includes(term))
    );
  }, [clients, clientSearch]);

  const filteredRequests = useMemo(() => {
    if (requestStatusFilter === 'todas') return requests;
    if (requestStatusFilter === 'abiertas') return requests.filter((r) => OPEN_STATUSES.includes(r.status));
    return requests.filter((r) => r.status === requestStatusFilter);
  }, [requests, requestStatusFilter]);

  const inboxRows = useMemo(() => inboxMessages.map((message) => {
    const client = clients.find((item) => Number(item.id) === Number(message.client_id));
    return {
      ...message,
      client_name: client?.display_name || client?.name || `Cliente #${message.client_id}`,
      client_email: client?.email || ''
    };
  }), [inboxMessages, clients]);

  const approved = requests.filter((r) => ['approved', 'in_progress'].includes(r.status));
  const pendingCount = requests.filter((r) => ['pending', 'reviewing', 'returned'].includes(r.status)).length;

  const sectionsWithCounts = SECTIONS.map((item) =>
    item.id === 'solicitudes' ? { ...item, count: pendingCount }
      : item.id === 'mensajes' ? { ...item, count: inboxMessages.length }
      : item.id === 'aprobados' ? { ...item, count: approved.length }
        : item
  );

  const requestColumns = [
    {
      key: 'title',
      header: 'Solicitud',
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate font-semibold text-navy-900">{row.title}</p>
          <p className="truncate text-xs text-navy-900/50">{row.client_name}</p>
        </div>
      )
    },
    { key: 'status', header: 'Estado', render: (row) => <Badge tone={REQUEST_STATUS[row.status]?.tone}>{REQUEST_STATUS[row.status]?.label || row.status}</Badge> },
    { key: 'priority', header: 'Prioridad', render: (row) => (row.priority === 'urgent' ? <Badge tone="magenta">Urgente</Badge> : row.priority === 'high' ? <Badge tone="gold">Pronto</Badge> : <span className="text-navy-900/50">Normal</span>) },
    { key: 'created_at', header: 'Recibida', render: (row) => formatDate(row.created_at) }
  ];

  return (
    <div className="operation-marketos relative mx-auto max-w-7xl space-y-5 overflow-hidden p-3 sm:p-5">
      <div className="operation-marketos__orb operation-marketos__orb--sky" aria-hidden="true" />
      <div className="operation-marketos__orb operation-marketos__orb--pink" aria-hidden="true" />

      {/* El panel se comporta como el centro de comando de MARKETOS: cada
          señal importante tiene color, jerarquía y una lectura inmediata. */}
      <section className="operation-marketos__hero relative overflow-hidden rounded-[1.75rem] border border-white/20 bg-[#071D53] p-5 text-white shadow-soft sm:p-7">
        <div className="absolute -right-12 -top-16 h-56 w-56 rounded-full bg-sky/30 blur-3xl" aria-hidden="true" />
        <div className="absolute -bottom-20 left-1/3 h-48 w-48 rounded-full bg-magenta/30 blur-3xl" aria-hidden="true" />
        <div className="relative flex flex-wrap items-start justify-between gap-5">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.16em] text-gold-50">
              <Sparkles className="h-3.5 w-3.5 text-gold" /> Centro de control
            </span>
            <h1 className="mt-4 text-2xl font-black tracking-tight sm:text-3xl">Operación MARKETOS</h1>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-white/75 sm:text-base">
              Desde aquí decides qué avanza, qué se mejora y cómo crece cada cliente.
              Nada importante se ejecuta sin tu aprobación.
            </p>
          </div>
          <Button variant="ghost" size="sm" icon={<RefreshCw className="h-4 w-4" />} onClick={load} className="!border-white/25 !bg-white/15 !text-white hover:!bg-white/25">
            Actualizar datos
          </Button>
        </div>

        <div className="relative mt-6 grid gap-3 sm:grid-cols-3">
          <Signal label="Tu decisión" value={`${pendingCount} pendientes`} icon={<Zap className="h-4 w-4" />} tone="gold" onClick={() => setSection('solicitudes')} />
          <Signal label="En movimiento" value={`${approved.length} aprobados`} icon={<Activity className="h-4 w-4" />} tone="sky" onClick={() => setSection('aprobados')} />
          <Signal label="Operación" value={`${metrics?.totals?.clients ?? 0} clientes`} icon={<CheckCircle2 className="h-4 w-4" />} tone="pink" onClick={() => setSection('clientes')} />
        </div>
      </section>

      {notice && <Alert tone={notice.tone} onDismiss={() => setNotice(null)}>{notice.text}</Alert>}

      <Tabs className="marketos-operation-tabs" tabs={sectionsWithCounts} value={section} onChange={setSection} />

      <AsyncBoundary loading={loading} error={error} onRetry={load} loadingLabel="Cargando la operación…">
        <div className="animate-fade-in">
          {section === 'resumen' && (
            <div className="space-y-5">
              <div className="operation-marketos__stats">
                <StatGrid>
                  <StatCard label="Clientes activos" value={metrics?.totals?.clients ?? 0} tone="navy" />
                  <StatCard label="Solicitudes pendientes" value={metrics?.totals?.pending_requests ?? 0} tone="gold" hint="Esperan tu revisión" onClick={() => setSection('solicitudes')} />
                  <StatCard label="Cambios completados" value={metrics?.totals?.completed_changes ?? 0} tone="green" />
                  <StatCard label="Productos contratados" value={metrics?.totals?.contracted_services ?? 0} tone="grape" onClick={() => setSection('contratados')} />
                </StatGrid>
              </div>

              <div className="grid gap-5 lg:grid-cols-2">
                <Card className="marketos-operation-card marketos-operation-card--decision">
                  <CardHeader title="Necesitan tu decisión" subtitle="Solicitudes esperando revisión o aprobación." icon={<ClipboardList className="h-5 w-5" />} />
                  <div className="p-2 sm:p-3">
                    <DataTable
                      rowKey={(row) => row.id}
                      rows={requests.filter((r) => ['pending', 'reviewing', 'returned'].includes(r.status)).slice(0, 8)}
                      columns={requestColumns.slice(0, 3)}
                      onRowClick={(row) => setOpenRequestId(row.id)}
                      emptyMessage="Nada pendiente. Todo al día."
                    />
                  </div>
                </Card>

                <Card className="marketos-operation-card marketos-operation-card--progress">
                  <CardHeader title="Aprobadas, listas para ejecutar" subtitle="Trabajo autorizado que aún no se ha aplicado." icon={<ShieldCheck className="h-5 w-5" />} />
                  <div className="p-2 sm:p-3">
                    <DataTable
                      rowKey={(row) => row.id}
                      rows={approved.slice(0, 8)}
                      columns={requestColumns.slice(0, 3)}
                      onRowClick={(row) => setOpenRequestId(row.id)}
                      emptyMessage="No hay trabajo aprobado pendiente."
                    />
                  </div>
                </Card>
              </div>
            </div>
          )}

          {section === 'clientes' && (
            <Card className="marketos-operation-card">
              <CardHeader
                icon={<Users className="h-5 w-5" />}
                title="Clientes"
                subtitle="Abre una ficha para ver productos, métricas, solicitudes y auditoría de ese cliente."
                actions={
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-navy-900/35" />
                    <Input
                      value={clientSearch}
                      onChange={(event) => setClientSearch(event.target.value)}
                      placeholder="Buscar cliente…"
                      className="!pl-9"
                      aria-label="Buscar cliente"
                    />
                  </div>
                }
              />
              <div className="p-2 sm:p-3">
                <DataTable
                  rowKey={(row) => row.id}
                  rows={filteredClients}
                  onRowClick={(row) => setOpenClientId(row.id)}
                  emptyMessage={clientSearch ? 'Ningún cliente coincide con tu búsqueda' : 'Todavía no hay clientes'}
                  columns={[
                    {
                      key: 'name',
                      header: 'Cliente',
                      render: (row) => (
                        <div className="min-w-0">
                          <p className="truncate font-semibold text-navy-900">{row.display_name || row.name}</p>
                          <p className="truncate text-xs text-navy-900/50">{row.email}</p>
                        </div>
                      )
                    },
                    { key: 'industry', header: 'Sector', render: (row) => row.industry || row.category || '—' },
                    {
                      key: 'service_status',
                      header: 'Servicio',
                      render: (row) => <Badge tone={row.service_status === 'active' ? 'green' : row.service_status === 'paused' ? 'gold' : 'sky'}>{row.service_status || 'onboarding'}</Badge>
                    },
                    { key: 'assigned_to', header: 'Responsable', render: (row) => row.assigned_to || 'Sin asignar' },
                    { key: 'start_date', header: 'Inicio', render: (row) => formatDate(row.start_date) }
                  ]}
                />
              </div>
            </Card>
          )}

          {section === 'catalogo' && <CatalogoProductos onCatalogChange={load} />}

          {section === 'contratados' && (
            <ProductosContratados clients={clients} onOpenClient={setOpenClientId} />
          )}

          {section === 'solicitudes' && (
            <Card className="marketos-operation-card">
              <CardHeader
                icon={<ClipboardList className="h-5 w-5" />}
                title="Solicitudes de cambio"
                subtitle="La aprobación es exclusiva de MARKETOS. Nada se ejecuta sin pasar por aquí."
                actions={
                  <Select value={requestStatusFilter} onChange={(event) => setRequestStatusFilter(event.target.value)} aria-label="Filtrar por estado">
                    <option value="abiertas">Abiertas</option>
                    <option value="todas">Todas</option>
                    {REQUEST_STATUS_ORDER.map((status) => (
                      <option key={status} value={status}>{REQUEST_STATUS[status].label}</option>
                    ))}
                  </Select>
                }
              />
              <div className="p-2 sm:p-3">
                <DataTable
                  rowKey={(row) => row.id}
                  rows={filteredRequests}
                  columns={requestColumns}
                  onRowClick={(row) => setOpenRequestId(row.id)}
                  emptyMessage="No hay solicitudes en este estado"
                />
              </div>
            </Card>
          )}

          {section === 'mensajes' && (
            <Card className="marketos-operation-card">
              <CardHeader
                icon={<MessageSquare className="h-5 w-5" />}
                title="Bandeja de mensajes de clientes"
                subtitle="Aquí llegan los mensajes que cada cliente envía desde Mi Proyecto."
              />
              <div className="p-2 sm:p-3">
                <DataTable
                  rowKey={(row) => row.id}
                  rows={inboxRows}
                  columns={[
                    {
                      key: 'client_name',
                      header: 'Cliente',
                      render: (row) => (
                        <div className="min-w-0">
                          <p className="truncate font-semibold text-navy-900">{row.client_name}</p>
                          <p className="truncate text-xs text-navy-900/50">{row.client_email}</p>
                        </div>
                      )
                    },
                    { key: 'body', header: 'Mensaje', render: (row) => <p className="max-w-xl truncate text-navy-900/80">{row.body}</p> },
                    { key: 'created_at', header: 'Recibido', render: (row) => formatDateTime(row.created_at) }
                  ]}
                  onRowClick={(row) => setOpenClientId(row.client_id)}
                  emptyMessage="Todavía no hay mensajes enviados por clientes"
                />
              </div>
            </Card>
          )}

          {section === 'aprobados' && (
            <Card className="marketos-operation-card">
              <CardHeader
                icon={<ShieldCheck className="h-5 w-5" />}
                title="Cambios aprobados"
                subtitle="Trabajo autorizado. Abre cada uno para revisar la vista previa antes de ejecutarlo."
              />
              <div className="p-2 sm:p-3">
                <DataTable
                  rowKey={(row) => row.id}
                  rows={approved}
                  columns={[
                    ...requestColumns.slice(0, 2),
                    { key: 'approved_at', header: 'Aprobada', render: (row) => formatDate(row.approved_at) },
                    { key: 'assigned_employee', header: 'Responsable', render: (row) => row.assigned_employee || 'Sin asignar' }
                  ]}
                  onRowClick={(row) => setOpenRequestId(row.id)}
                  emptyMessage="No hay cambios aprobados pendientes de ejecución"
                />
              </div>
            </Card>
          )}

          {section === 'metricas' && <MetricasGlobales metrics={metrics} clients={clients} onReload={load} />}

          {section === 'auditoria' && <Auditoria clients={clients} />}

          {section === 'whatsapp' && <WhatsAppMarketos clients={clients} onConnected={load} />}

          {section === 'ia' && <OperacionesIA clients={clients} onProposed={load} />}

          {section === 'configuracion' && <ConfiguracionOperacion />}
        </div>
      </AsyncBoundary>

      <FichaCliente
        clientId={openClientId}
        open={!!openClientId}
        onClose={() => setOpenClientId(null)}
        onChanged={load}
      />

      <SolicitudDetalle
        requestId={openRequestId}
        open={!!openRequestId}
        onClose={() => setOpenRequestId(null)}
        onChanged={load}
      />
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────

function Signal({ label, value, icon, tone, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`operation-marketos__signal operation-marketos__signal--${tone}`}
    >
      <span className="operation-marketos__signal-icon">{icon}</span>
      <span className="min-w-0 text-left">
        <span className="block text-[10px] font-bold uppercase tracking-[0.14em] text-white/55">{label}</span>
        <span className="mt-0.5 block truncate text-sm font-extrabold text-white">{value}</span>
      </span>
    </button>
  );
}

// ───────────────────────────────────────────────────────────────────────────

// Vista transversal: todos los productos contratados de todos los clientes.
// Se arma desde el listado de clientes ya cargado (cada fila trae su perfil),
// así que no cuesta una petición extra.
function ProductosContratados({ clients, onOpenClient }) {
  const [detail, setDetail] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Una petición por cliente. Con la escala actual (decenas de clientes)
        // es aceptable; cuando MARKETOS pase de ~100 clientes esto debe
        // reemplazarse por un endpoint agregado en el backend.
        const results = await Promise.all(clients.map(async (client) => {
          try {
            const data = await adminService.getClient(client.id);
            return (data.services || []).map((service) => ({ ...service, client }));
          } catch (_) {
            return [];
          }
        }));
        if (!cancelled) setDetail(results.flat());
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clients]);

  return (
    <Card className="marketos-operation-card">
      <CardHeader
        icon={<ShieldCheck className="h-5 w-5" />}
        title="Productos contratados"
        subtitle="Qué tiene contratado cada cliente y en qué estado va su implementación."
      />
      <div className="p-2 sm:p-3">
        <AsyncBoundary loading={loading} error={error} empty={!detail.length} emptyProps={{ title: 'Todavía no hay productos asignados' }} loadingLabel="Reuniendo productos contratados…">
          <DataTable
            rowKey={(row) => row.assignment_id}
            rows={detail}
            onRowClick={(row) => onOpenClient(row.client.id)}
            columns={[
              { key: 'client', header: 'Cliente', render: (row) => <span className="font-semibold">{row.client.display_name || row.client.name}</span> },
              { key: 'name', header: 'Producto' },
              { key: 'price', header: 'Precio', render: (row) => formatMoney(row.agreed_price ?? row.base_price) },
              { key: 'service_status', header: 'Estado', render: (row) => <Badge tone="sky">{row.service_status}</Badge> }
            ]}
          />
        </AsyncBoundary>
      </div>
    </Card>
  );
}

// Métricas globales con filtro por cliente (spec §10): el administrador ve
// todo y puede acotar; el cliente nunca llega a esta pantalla.
function MetricasGlobales({ metrics: globalMetrics, clients }) {
  const [clientId, setClientId] = useState('');
  const [data, setData] = useState(globalMetrics);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!clientId) { setData(globalMetrics); return; }
    let cancelled = false;
    setLoading(true);
    adminService.metrics(clientId)
      .then((result) => { if (!cancelled) { setData(result); setError(null); } })
      .catch((err) => { if (!cancelled) setError(err); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId, globalMetrics]);

  const totals = data?.totals || {};

  return (
    <Card className="marketos-operation-card">
      <CardHeader
        icon={<BarChart3 className="h-5 w-5" />}
        title="Métricas"
        subtitle={clientId ? 'Datos de un solo cliente.' : 'Datos globales de toda la operación.'}
        actions={
          <Select value={clientId} onChange={(event) => setClientId(event.target.value)} aria-label="Filtrar por cliente">
            <option value="">Todos los clientes</option>
            {clients.map((client) => <option key={client.id} value={client.id}>{client.display_name || client.name}</option>)}
          </Select>
        }
      />
      <div className="space-y-5 p-4 sm:p-5">
        <AsyncBoundary loading={loading} error={error} loadingLabel="Calculando métricas…">
          <>
            <StatGrid>
              <StatCard label="Contactos nuevos" value={totals.contacts ?? 0} tone="sky" />
              <StatCard label="Conversaciones" value={totals.conversations ?? 0} tone="grape" />
              <StatCard label="Ventas" value={totals.sales ?? 0} tone="green" />
              <StatCard label="Ingresos" value={formatMoney(totals.revenue)} tone="magenta" />
              <StatCard label="Páginas activas" value={totals.active_sites ?? 0} tone="navy" />
              <StatCard label="Campañas activas" value={totals.active_campaigns ?? 0} tone="gold" />
              <StatCard label="Solicitudes pendientes" value={totals.pending_requests ?? 0} tone="gold" />
              <StatCard label="Cambios completados" value={totals.completed_changes ?? 0} tone="green" />
            </StatGrid>

            <div>
              <SectionTitle>Solicitudes por estado</SectionTitle>
              <div className="flex flex-wrap gap-2">
                {REQUEST_STATUS_ORDER.map((status) => (
                  <Badge key={status} tone={REQUEST_STATUS[status].tone}>
                    {REQUEST_STATUS[status].label}: {data?.requests?.[status] ?? 0}
                  </Badge>
                ))}
              </div>
            </div>
          </>
        </AsyncBoundary>
      </div>
    </Card>
  );
}

// Auditoría (spec §9). Es solo lectura por diseño: un registro que se puede
// editar no sirve como auditoría.
function Auditoria({ clients }) {
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clientId, setClientId] = useState('');
  const [actorType, setActorType] = useState('');
  const [offset, setOffset] = useState(0);
  const LIMIT = 50;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await adminService.audit({
        limit: LIMIT,
        offset,
        ...(clientId ? { clientId } : {}),
        ...(actorType ? { actorType } : {})
      });
      setEvents(result.events || []);
      setTotal(result.total || 0);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [clientId, actorType, offset]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setOffset(0); }, [clientId, actorType]);

  return (
    <Card className="marketos-operation-card">
      <CardHeader
        icon={<ScrollText className="h-5 w-5" />}
        title="Auditoría"
        subtitle="Quién hizo qué, sobre qué cliente y cuándo. No se puede editar ni borrar."
        actions={
          <>
            <Select value={clientId} onChange={(event) => setClientId(event.target.value)} aria-label="Filtrar por cliente">
              <option value="">Todos los clientes</option>
              {clients.map((client) => <option key={client.id} value={client.id}>{client.display_name || client.name}</option>)}
            </Select>
            <Select value={actorType} onChange={(event) => setActorType(event.target.value)} aria-label="Filtrar por origen">
              <option value="">Todo origen</option>
              {Object.entries(ACTOR_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </Select>
          </>
        }
      />
      <div className="p-2 sm:p-3">
        <AsyncBoundary loading={loading} error={error} onRetry={load} empty={!events.length} emptyProps={{ title: 'Sin registros para este filtro' }} loadingLabel="Cargando auditoría…">
          <>
            <DataTable
              rowKey={(row) => row.id}
              rows={events}
              columns={[
                { key: 'created_at', header: 'Fecha', render: (row) => formatDateTime(row.created_at) },
                { key: 'actor_type', header: 'Origen', render: (row) => <Badge tone={row.actor_type === 'client' ? 'sky' : row.actor_type === 'ai' || row.actor_type === 'mcp' ? 'grape' : 'navy'}>{ACTOR_LABELS[row.actor_type] || row.actor_type}</Badge> },
                { key: 'client_name', header: 'Cliente' },
                { key: 'event_type', header: 'Acción', render: (row) => EVENT_LABELS[row.event_type] || row.event_type },
                { key: 'request_title', header: 'Solicitud', render: (row) => row.request_title || '—' },
                { key: 'service_name', header: 'Producto', render: (row) => row.service_name || '—' },
                { key: 'result', header: 'Resultado', render: (row) => (row.event_type === 'execution_failed' ? <Badge tone="magenta">Falló</Badge> : <Badge tone="green">OK</Badge>) }
              ]}
            />
            {total > LIMIT && (
              <div className="flex items-center justify-between gap-3 px-2 py-3 text-sm text-navy-900/60">
                <span>Mostrando {offset + 1}–{Math.min(offset + LIMIT, total)} de {total}</span>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMIT))}>Anterior</Button>
                  <Button variant="ghost" size="sm" disabled={offset + LIMIT >= total} onClick={() => setOffset(offset + LIMIT)}>Siguiente</Button>
                </div>
              </div>
            )}
          </>
        </AsyncBoundary>
      </div>
    </Card>
  );
}

// Las reglas que protegen la operación no son configurables. Sí lo son las
// herramientas operativas de MARKETOS, como la IA global que genera preguntas
// a medida para la ayuda guiada de los clientes.
function ConfiguracionOperacion() {
  const [guidedAi, setGuidedAi] = useState({
    enabled: false, has_api_key: false, base_url: 'https://api.openai.com/v1', model: 'gpt-5-mini'
  });
  const [guidedAiKey, setGuidedAiKey] = useState('');
  const [guidedAiLoading, setGuidedAiLoading] = useState(true);
  const [guidedAiBusy, setGuidedAiBusy] = useState(false);
  const [guidedAiNotice, setGuidedAiNotice] = useState(null);
  const guidedAiErrorMessage = (error) => error?.kind === 'not_found'
    ? 'El servidor todavía no tiene la actualización de ayuda guiada. Despliega primero el backend y vuelve a cargar esta página.'
    : error.message;

  const loadGuidedAi = useCallback(async () => {
    setGuidedAiLoading(true);
    try {
      const settings = await adminService.guidedAiSettings();
      setGuidedAi(settings);
      setGuidedAiNotice(null);
    } catch (error) {
      setGuidedAiNotice({ tone: 'danger', text: guidedAiErrorMessage(error) });
    } finally {
      setGuidedAiLoading(false);
    }
  }, []);

  useEffect(() => { loadGuidedAi(); }, [loadGuidedAi]);

  const saveGuidedAi = async () => {
    setGuidedAiBusy(true);
    setGuidedAiNotice(null);
    try {
      const saved = await adminService.saveGuidedAiSettings({
        enabled: guidedAi.enabled,
        apiKey: guidedAiKey,
        baseUrl: guidedAi.base_url,
        model: guidedAi.model
      });
      setGuidedAi(saved);
      setGuidedAiKey('');
      setGuidedAiNotice({ tone: 'success', text: saved.message || 'Configuración guardada.' });
    } catch (error) {
      setGuidedAiNotice({ tone: 'danger', text: guidedAiErrorMessage(error) });
    } finally {
      setGuidedAiBusy(false);
    }
  };

  const testGuidedAi = async () => {
    setGuidedAiBusy(true);
    setGuidedAiNotice(null);
    try {
      const result = await adminService.testGuidedAiSettings();
      setGuidedAiNotice(result.success
        ? { tone: 'success', text: result.message }
        : { tone: 'danger', text: result.error || 'No pudimos validar la conexión.' });
    } catch (error) {
      setGuidedAiNotice({ tone: 'danger', text: guidedAiErrorMessage(error) });
    } finally {
      setGuidedAiBusy(false);
    }
  };

  const disconnectGuidedAi = async () => {
    if (!window.confirm('¿Desconectar la IA global de ayuda guiada? Los clientes podrán seguir enviando solicitudes manuales.')) return;
    setGuidedAiBusy(true);
    setGuidedAiNotice(null);
    try {
      const saved = await adminService.disconnectGuidedAiSettings();
      setGuidedAi(saved);
      setGuidedAiKey('');
      setGuidedAiNotice({ tone: 'success', text: saved.message });
    } catch (error) {
      setGuidedAiNotice({ tone: 'danger', text: guidedAiErrorMessage(error) });
    } finally {
      setGuidedAiBusy(false);
    }
  };

  const RULES = [
    ['Aprobación obligatoria', 'Ninguna solicitud puede ejecutarse sin haber sido aprobada antes. El botón se deshabilita en pantalla y el servidor lo vuelve a verificar.'],
    ['Aislamiento por cliente', 'Cada cliente solo puede ver su propia información. El filtro se aplica en el servidor con la identidad de la sesión, no con un dato que el cliente pueda cambiar.'],
    ['La IA no decide', 'La IA y el MCP pueden analizar y proponer, pero sus propuestas entran como solicitudes pendientes de aprobación humana.'],
    ['Nada se borra', 'Rechazar, cancelar o archivar nunca elimina el registro. Todo movimiento queda en la auditoría.'],
    ['Los empleados no se autoaprueban', 'Un empleado puede avanzar el trabajo asignado, pero la aprobación es siempre del administrador de MARKETOS.']
  ];

  return (
    <div className="space-y-5">
      <Card className="marketos-operation-card overflow-hidden border-sky-200 bg-gradient-to-br from-sky-50 via-white to-grape-50 shadow-soft">
        <CardHeader
          icon={<Bot className="h-5 w-5" />}
          title="IA de ayuda guiada"
          subtitle="Esta IA es de MARKETOS: prepara preguntas a medida para los clientes. No usa ni revela las API keys que cada cliente conecte para sus propias automatizaciones."
        />
        <div className="space-y-4 bg-white/70 p-4 sm:p-5">
          {guidedAiNotice && <Alert tone={guidedAiNotice.tone} onDismiss={() => setGuidedAiNotice(null)}>{guidedAiNotice.text}</Alert>}
          {guidedAiLoading ? (
            <p className="text-sm text-navy-900/60">Cargando configuración de ayuda guiada…</p>
          ) : (
            <>
              <Alert tone={guidedAi.enabled && guidedAi.has_api_key ? 'success' : 'warning'} title={guidedAi.enabled && guidedAi.has_api_key ? 'Ayuda guiada activa' : 'Ayuda guiada desactivada'}>
                {guidedAi.enabled && guidedAi.has_api_key
                  ? 'Los clientes podrán recibir preguntas personalizadas antes de enviar una solicitud. Todas las propuestas seguirán pasando por tu aprobación.'
                  : 'Los clientes aún pueden enviar una solicitud manual. No verán preguntas genéricas como si fueran personalizadas.'}
              </Alert>
              <div className="grid gap-4 rounded-2xl border border-sky-100 bg-white/90 p-4 shadow-sm lg:grid-cols-2">
                <label className="block rounded-xl bg-navy-50 p-3 text-sm font-bold text-navy-900">
                  Estado
                  <span className="mt-1.5 block"><Select value={guidedAi.enabled ? 'active' : 'inactive'} onChange={(event) => setGuidedAi((current) => ({ ...current, enabled: event.target.value === 'active' }))}>
                      <option value="inactive">Desactivada</option>
                      <option value="active">Activada</option>
                    </Select></span>
                </label>
                <label className="block rounded-xl bg-grape-50 p-3 text-sm font-bold text-navy-900">
                  Modelo
                  <span className="mt-1.5 block"><Input value={guidedAi.model || ''} onChange={(event) => setGuidedAi((current) => ({ ...current, model: event.target.value }))} placeholder="Ej.: gpt-5-mini" /></span>
                </label>
              </div>
              <label className="block rounded-2xl border border-navy-100 bg-white/90 p-4 text-sm font-bold text-navy-900 shadow-sm">
                URL base del proveedor
                <span className="mt-1.5 block"><Input value={guidedAi.base_url || ''} onChange={(event) => setGuidedAi((current) => ({ ...current, base_url: event.target.value }))} placeholder="https://api.openai.com/v1" /></span>
              </label>
              <label className="block rounded-2xl border border-magenta-100 bg-magenta-50/50 p-4 text-sm font-bold text-navy-900 shadow-sm">
                API key de MARKETOS
                <span className="mt-1.5 block"><Input type="password" value={guidedAiKey} onChange={(event) => setGuidedAiKey(event.target.value)} placeholder={guidedAi.has_api_key ? 'Hay una clave guardada. Escribe otra solo para reemplazarla.' : 'Pega aquí la API key global de MARKETOS'} autoComplete="new-password" /></span>
                <span className="mt-1 block text-xs font-normal text-navy-900/55">La clave se guarda cifrada y no vuelve a mostrarse. Dejar este campo vacío conserva la clave actual.</span>
              </label>
              <div className="flex flex-wrap justify-end gap-2 rounded-2xl border border-navy-100 bg-navy-50 p-3">
                {guidedAi.has_api_key && <Button variant="soft" disabled={guidedAiBusy} onClick={disconnectGuidedAi}>Desconectar IA</Button>}
                <Button variant="soft" loading={guidedAiBusy} disabled={!guidedAi.enabled || !guidedAi.has_api_key} onClick={testGuidedAi}>Probar conexión</Button>
                <Button loading={guidedAiBusy} onClick={saveGuidedAi}>Guardar configuración</Button>
              </div>
            </>
          )}
        </div>
      </Card>

      <Card className="marketos-operation-card">
        <CardHeader icon={<Settings2 className="h-5 w-5" />} title="Reglas de operación" subtitle="Estas garantías no son configurables a propósito." />
        <div className="space-y-3 p-4 sm:p-5">
          {RULES.map(([title, description]) => (
            <div key={title} className="rounded-xl border border-navy-50 bg-cream p-4">
              <p className="flex items-center gap-2 font-bold text-navy-900">
                <ShieldCheck className="h-4 w-4 text-magenta" /> {title}
              </p>
              <p className="mt-1 text-sm text-navy-900/65">{description}</p>
            </div>
          ))}
          <EmptyState
            title="¿Falta algo aquí?"
            message="Los ajustes de cuenta, cobros y accesos viven en la sección de clientes del Gestor SaaS."
          />
        </div>
      </Card>
    </div>
  );
}
